import { Link } from 'react-router-dom';
import { MAIN_SITE_URL, categories } from '@/mocks/categories';
import { articleList } from '@/mocks/articles';
import JsonLd from '@/components/base/JsonLd';

const SITE_URL = 'https://vigorbuyspread.site';
const PAGE_URL = `${SITE_URL}/vigorbuy-spreadsheet-guide`;

const guideJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      headline: 'The Complete VigorBuy Spreadsheet Guide: Master Spreadsheet Shopping in 2026',
      description: 'Learn everything about VigorBuy Spreadsheet — from getting started to advanced product discovery strategies. Explore organized spreadsheets, QC photos, and the best shopping guides.',
      url: PAGE_URL,
      datePublished: '2026-07-22',
      dateModified: '2026-07-22',
      author: {
        '@type': 'Organization',
        name: 'VigorBuy Spreadsheet',
        url: SITE_URL,
      },
      publisher: {
        '@type': 'Organization',
        name: 'VigorBuy Spreadsheet',
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': PAGE_URL,
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'VigorBuy Spreadsheet Guide',
          item: PAGE_URL,
        },
      ],
    },
  ],
};

export default function GuideArticle() {
  return (
    <main>
      <JsonLd data={guideJsonLd} />
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/95 backdrop-blur-md border-b border-background-200/50">
        <nav className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 cursor-pointer group">
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-database-2-line text-lg text-white"></i>
              </div>
              <span className="font-heading text-lg text-foreground-950 whitespace-nowrap group-hover:text-primary-600 transition-colors">
                VigorBuy Spreadsheet
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-3">
              <Link to="/" className="px-3 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors rounded-md whitespace-nowrap cursor-pointer">
                Home
              </Link>
              <Link to="/vigorbuy-spreadsheet-guide" className="px-3 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors rounded-md whitespace-nowrap cursor-pointer font-medium">
                Shopping Guide
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="px-4 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors rounded-md whitespace-nowrap cursor-pointer md:hidden">
                <i className="ri-home-4-line mr-1"></i> Home
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </nav>
      </header>

      <article className="pt-16">
        <header className="bg-background-100 py-16 md:py-20">
          <div className="max-w-[960px] mx-auto px-4 md:px-6 lg:px-10">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Spreadsheet Shopping</span>
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">Beginner Guide</span>
              <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">2026 Edition</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">
              The Complete <strong className="font-heading font-normal text-primary-600">VigorBuy Spreadsheet</strong> Guide: Master Spreadsheet Shopping in 2026
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              Are you tired of endlessly scrolling through product listings, losing track of items you love, and struggling to compare quality across different sellers? Enter the world of spreadsheet shopping — a smarter, faster, and radically more organized approach to discovering products online. In this comprehensive guide, we will walk you through everything you need to know about VigorBuy Spreadsheet, from getting started to advanced product discovery strategies.
            </p>
          </div>
        </header>

        <div className="max-w-[960px] mx-auto px-4 md:px-6 lg:px-10 py-12 md:py-16">
          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">The Evolution of Online Shopping Discovery</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              Traditional online shopping works reasonably well when you know exactly what you want. Type a product name into a search bar, scroll through results, and click purchase. But what happens when you want to browse, compare, and discover? That is where the experience falls apart. Search results get cluttered with sponsored listings, product pages scatter information across multiple tabs, and there is no easy way to compare five different sneakers side by side without opening five browser windows.
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              The spreadsheet shopping movement emerged organically from communities of passionate shoppers who wanted a better way. Instead of wrestling with search algorithms, they began compiling product databases in organized spreadsheets — complete with photos, prices, sizes, direct links, and quality control notes. What started as a grassroots hack has evolved into a legitimate product discovery ecosystem, with VigorBuy Spreadsheet at the forefront of this revolution.
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              The brilliance of the spreadsheet approach lies in its structured simplicity. Every product occupies one row. Every important detail gets its own column. You can sort by price with a single click, filter by category in seconds, and compare products visually without losing your place. It transforms shopping from a chaotic treasure hunt into an organized, almost meditative browsing experience — and once you have tried it, it is genuinely hard to go back.
            </p>
          </section>

          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">What Makes VigorBuy Spreadsheet Different</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              Not all product spreadsheets are created equal. What sets VigorBuy Spreadsheet apart is the depth and care that goes into organizing each product entry. Every item in the database is categorized, tagged with relevant keywords, and paired with QC (Quality Control) photos so you can inspect the actual product — not just marketing images — before committing to a purchase.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 my-8">
              {[
                { title: 'Comprehensive Categorization', desc: 'Products are organized across 14+ categories including Shoes, Hoodies, T-Shirts, Jackets, Accessories, Bags, and more — making it effortless to find exactly what you are looking for.' },
                { title: 'Built-in QC Photos', desc: 'Every product entry includes real quality control photographs, giving you an honest preview of what you will actually receive rather than relying on polished marketing shots.' },
                { title: 'Regular Database Updates', desc: 'The spreadsheet is continuously refreshed with new product discoveries, trending items, and seasonal collections so you always have access to current information.' },
                { title: 'Side-by-Side Comparison', desc: 'The structured spreadsheet format makes it natural to compare products across multiple dimensions — price, quality, style, and availability — all in one view.' },
              ].map((item) => (
                <div key={item.title} className="p-5 md:p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <h3 className="font-heading text-sm md:text-base text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              The combination of thorough categorization, QC photo integration, and regular updates creates a shopping experience that is genuinely superior to traditional browsing. You spend less time searching and more time discovering products you actually want — and that efficiency is what keeps the spreadsheet shopping community growing.
            </p>
          </section>

          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">Getting Started: Your First Spreadsheet Shopping Session</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              If you are new to spreadsheet-based product discovery, the process is refreshingly straightforward. Here is a step-by-step walkthrough for your first session:
            </p>

            <div className="space-y-4 my-8">
              {[
                { step: '01', title: 'Choose Your Category', desc: 'Start by selecting a product category that interests you — Shoes, Hoodies, Accessories, or any of the 14 available categories. Each category opens a dedicated product database filtered to show only relevant items.' },
                { step: '02', title: 'Browse the Organized List', desc: 'Scroll through the spreadsheet view where each row represents a product. Notice how product names, prices, sizes, and availability are all neatly organized in columns for easy scanning.' },
                { step: '03', title: 'Inspect QC Photos', desc: 'For products you are interested in, take a moment to review the attached QC photos. These real product images give you an honest preview of materials, stitching, colors, and overall quality.' },
                { step: '04', title: 'Compare Side by Side', desc: 'Found multiple items you like? The spreadsheet format lets you easily compare them — glance across rows to evaluate price differences, size availability, and style variations at the same time.' },
                { step: '05', title: 'Make Your Selection', desc: 'Once you have found the perfect product, follow the direct link to complete your purchase on the shopping platform. The organized format means you never lose track of your top picks.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-4 p-5 bg-white rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading text-sm md:text-base text-primary-600">{item.step}</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm md:text-base text-foreground-900 mb-1.5">{item.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">VigorBuy Spreadsheet vs Traditional Shopping</h2>
            <div className="overflow-x-auto bg-white rounded-xl border border-background-200/70">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/50 bg-background-50">
                    <th className="text-left px-5 md:px-6 py-3.5 text-xs md:text-sm font-semibold text-foreground-700 whitespace-nowrap">Aspect</th>
                    <th className="text-left px-5 md:px-6 py-3.5 text-xs md:text-sm font-semibold text-foreground-500 whitespace-nowrap">Traditional Shopping</th>
                    <th className="text-left px-5 md:px-6 py-3.5 text-xs md:text-sm font-semibold text-accent-700 whitespace-nowrap">VigorBuy Spreadsheet</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { aspect: 'Search Speed', traditional: 'Manual scrolling through pages', spreadsheet: 'Instant filtered browsing' },
                    { aspect: 'Product Organization', traditional: 'Scattered across multiple tabs', spreadsheet: 'One unified database view' },
                    { aspect: 'Quality Verification', traditional: 'Rely on marketing images', spreadsheet: 'Real QC photos included' },
                    { aspect: 'Price Comparison', traditional: 'Switch between pages', spreadsheet: 'Side-by-side in one view' },
                    { aspect: 'Discovery', traditional: 'Limited by search algorithm', spreadsheet: 'Browse entire curated collections' },
                    { aspect: 'Tracking', traditional: 'Bookmarks and open tabs', spreadsheet: 'Organized rows with notes' },
                    { aspect: 'Update Frequency', traditional: 'Depends on platform', spreadsheet: 'Regularly refreshed' },
                    { aspect: 'Learning Curve', traditional: 'None required', spreadsheet: 'Minimal — intuitive format' },
                  ].map((row) => (
                    <tr key={row.aspect} className="border-b border-background-100 last:border-0 hover:bg-background-50/50 transition-colors">
                      <td className="px-5 md:px-6 py-3 text-xs md:text-sm font-medium text-foreground-800 whitespace-nowrap">{row.aspect}</td>
                      <td className="px-5 md:px-6 py-3 text-xs md:text-sm text-foreground-500">{row.traditional}</td>
                      <td className="px-5 md:px-6 py-3 text-xs md:text-sm font-medium text-accent-700">{row.spreadsheet}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">Safety Tips for Spreadsheet Shopping</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              While the spreadsheet shopping method is inherently organized and transparent, it is still important to follow basic safety practices. The spreadsheet itself is purely a product information database — it does not process payments or store personal data — but the products you discover will be purchased through external platforms.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
              {[
                { tip: 'Always verify the product link matches the spreadsheet entry before purchasing.', icon: 'ri-link' },
                { tip: 'Review QC photos carefully — look at stitching, materials, and color accuracy.', icon: 'ri-zoom-in-line' },
                { tip: 'Use secure payment methods that offer buyer protection for your transactions.', icon: 'ri-shield-check-line' },
                { tip: 'Keep a personal copy of products you are interested in for easy reference.', icon: 'ri-bookmark-line' },
                { tip: 'Check product availability and sizing information before committing to a purchase.', icon: 'ri-ruler-line' },
                { tip: 'Never share your account credentials or payment details with unverified third parties.', icon: 'ri-lock-line' },
              ].map((item) => (
                <div key={item.tip} className="flex gap-3 p-4 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-sm text-accent-600`}></i>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.tip}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">Explore Product Categories</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
              VigorBuy Spreadsheet covers an extensive range of product categories, each with its own curated database. Here are all the categories you can explore right now:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 px-4 py-3 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm text-accent-600`}></i>
                  </div>
                  <span className="text-xs md:text-sm font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                </a>
              ))}
            </div>
          </section>

          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                {
                  q: 'Do I need any special software to use the spreadsheet?',
                  a: 'No special software is required. The VigorBuy Spreadsheet is accessible through any modern web browser. Simply click on a category link and you will be taken directly to the organized product database. No downloads, installations, or accounts needed to start browsing.',
                },
                {
                  q: 'How current is the product information?',
                  a: 'The product database receives regular updates to keep listings current. New products are added frequently, prices are refreshed on an ongoing basis, and unavailable items are removed. The update cadence varies by category, with high-demand sections receiving more frequent attention.',
                },
                {
                  q: 'Can I request specific products to be added?',
                  a: 'The spreadsheet is curated to include a wide variety of products across all major categories. While there is no direct product request system, the database is regularly expanded based on trending items and community interest, so popular product types are well-represented.',
                },
              ].map((faq) => (
                <div key={faq.q} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <h3 className="font-heading text-sm md:text-base text-foreground-900 mb-2">{faq.q}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="border-t border-background-200 pt-12">
            <div className="bg-gradient-to-r from-primary-50 via-accent-50 to-secondary-50 rounded-2xl p-8 md:p-10 text-center">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">
                Ready to Start Exploring?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto mb-6">
                Dive into the organized world of spreadsheet shopping. Browse categories, compare products, and discover your next favorite find.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-7 py-3 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-7 py-3 bg-white text-primary-600 font-semibold text-sm rounded-full border border-primary-200 hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  VigorBuy Spreadsheet <i className="ri-arrow-right-up-line ml-1 text-xs"></i>
                </a>
              </div>
            </div>
          </section>

          <section className="mt-10 pt-8 border-t border-background-200/50">
            <h3 className="font-heading text-base text-foreground-700 mb-4">Explore More Expert Guides</h3>
            <div className="flex flex-wrap gap-2">
              <Link to="/" className="px-4 py-2 bg-background-100 rounded-lg text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
                Homepage
              </Link>
              {articleList.map((a) => (
                <Link
                  key={a.slug}
                  to={`/articles/${a.slug}`}
                  className="px-4 py-2 bg-background-100 rounded-lg text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {a.tags[0]?.label || 'Guide'}
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>

      <footer className="bg-background-950 text-white">
        <div className="max-w-[960px] mx-auto px-4 md:px-6 lg:px-10 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2 cursor-pointer group">
              <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-database-2-line text-base text-white"></i>
              </div>
              <span className="font-heading text-base text-white group-hover:text-primary-300 transition-colors">
                VigorBuy Spreadsheet
              </span>
            </Link>
            <p className="text-xs text-foreground-300">
              &copy; 2026 VigorBuy Spreadsheet. All rights reserved. | <Link to="/" className="hover:text-primary-300 transition-colors cursor-pointer">Return to Homepage</Link>
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}